import { useCallback, useEffect, useMemo, useState } from 'react';
import { ChevronIcon, PencilIcon, PlusIcon, TrashIcon } from '@/components/catalog/icons';
import { FormErrorSummary } from '@/components/catalog/FormShell';
import { adminWriteDenial, canWriteAdminMaster } from '@/components/catalog-admin/admin-write';
import {
  CatalogErrorBlock,
  CatalogMasterCard,
  CatalogStatusBadge,
} from '@/components/catalog-admin/CatalogMasterBits';
import { ApiError, api } from '@/lib/api';
import { cx } from '@/lib/cx';
import { pluralise } from '@/lib/format';
import type { Ability } from '@/lib/permissions';
import { invalidateCompanyStageStatuses } from '@/lib/use-company-stage-statuses';
import type {
  CompanyStageStatus,
  CompanyStageStatusCreateInput,
  CompanyStageStatusUpdateInput,
  MasterRef,
  ReferenceRecord,
  StageStatusResurfaceMode,
} from '@/lib/types';
import {
  Badge,
  Button,
  Checkbox,
  ConfirmDialog,
  EmptyState,
  Field,
  Input,
  Modal,
  Select,
  Skeleton,
  Table,
  TableWrap,
  TBody,
  TD,
  TH,
  THead,
  TR,
} from '@/components/ui';

/* -------------------------------------------------------------------------
 * Company stage statuses, managed from /catalog (owner 2026-10-23).
 *
 * The "Stage status" drill-down under each company stage
 * (`f_company_stage_statuses`, migration 301). Starts EMPTY: no seed rows.
 *
 * ## Why a card of its own rather than a `MASTERS` entry
 *
 * A registry master is exactly `{id, name, sortOrder, isActive}`; this row also
 * carries a `stage`, which is set on create and refused on PATCH, and the list
 * is read GROUPED by stage. The endpoint is not paged either.
 *
 * Writes need `reference-sales` (the grant `company-stages` uses). After every
 * write the shared picker cache in `use-company-stage-statuses.ts` is dropped,
 * so a company form opened next sees the change.
 *
 * Refusals, matched on `error.code`:
 *  - `STAGE_STATUS_NAME_TAKEN` (409): the name is already used in that stage.
 *  - `STAGE_STATUS_IN_USE` (409, delete): a company still holds it — retire.
 *  - `STAGE_STATUS_BEHAVIOUR_IN_USE` (409, save): the behaviour change is
 *    refused while it is in use; the server's message is shown at form level.
 *  - `STAGE_STATUS_BEHAVIOUR_CONFLICT` (422): a follow-up SLA and a resurface
 *    mode are both set after the merge. The form makes them mutually exclusive
 *    (setting one clears the other), so this is only a race with another edit.
 *
 * ## Behaviour flags (owner 2026-10-23, migration 303)
 *
 * `followUpSlaDays` (1..365), `resurfaceMode` ('date' | 'next_cycle'),
 * `excludesFollowUp`, `requiresReason`, `suppressesMarketing`. Behaviour is
 * read from these flags, never from the (admin-renamable) name. An older API
 * omits them (`undefined`): the row shows no behaviour badges, and the PATCH is
 * diffed against the form's initial values so an untouched field is never sent.
 * --------------------------------------------------------------------- */

const MODULE = 'reference-sales' as const;
const SKELETON_ROWS = 4;

/** `sort_order` is `SMALLINT UNSIGNED`. */
const SORT_ORDER_MIN = 0;
const SORT_ORDER_MAX = 65535;

/** `name` is `VARCHAR(60)`. */
const NAME_MAX = 60;

/** `follow_up_sla_days`: the server accepts 1..365. */
const SLA_MIN = 1;
const SLA_MAX = 365;

const RESURFACE_OPTIONS: readonly { value: '' | StageStatusResurfaceMode; label: string }[] = [
  { value: '', label: 'None' },
  { value: 'date', label: 'On a chosen date' },
  { value: 'next_cycle', label: 'Before next cycle (1 Mar)' },
];

/** Compact behaviour badges for a row. Nothing when the API omits the flags. */
function BehaviourBadges({ row }: { row: CompanyStageStatus }) {
  const badges: { key: string; label: string; tone: 'neutral' | 'info' | 'warning' }[] = [];
  if (typeof row.followUpSlaDays === 'number') {
    badges.push({ key: 'sla', label: pluralise(row.followUpSlaDays, 'day', 'days'), tone: 'info' });
  }
  if (row.resurfaceMode === 'date') {
    badges.push({ key: 'resurface', label: 'Resurfaces on date', tone: 'info' });
  } else if (row.resurfaceMode === 'next_cycle') {
    badges.push({ key: 'resurface', label: 'Next cycle', tone: 'info' });
  }
  if (row.excludesFollowUp === true) badges.push({ key: 'excluded', label: 'Excluded', tone: 'neutral' });
  if (row.requiresReason === true) badges.push({ key: 'reason', label: 'Reason required', tone: 'neutral' });
  if (row.suppressesMarketing === true) {
    badges.push({ key: 'marketing', label: 'No marketing', tone: 'warning' });
  }
  if (badges.length === 0) return <span className="text-xs text-fg-subtle">—</span>;
  return (
    <div className="flex flex-wrap gap-1">
      {badges.map((badge) => (
        <Badge key={badge.key} tone={badge.tone} variant="outline">
          {badge.label}
        </Badge>
      ))}
    </div>
  );
}

interface ListState {
  rows: CompanyStageStatus[] | null;
  error: string | null;
  loading: boolean;
}

interface StageGroup {
  stage: MasterRef;
  /** The stage master row, when the page's stage list holds it. */
  stageActive: boolean | null;
  rows: CompanyStageStatus[];
}

/**
 * Group the statuses by stage, in the stage master's order. Every stage gets a
 * group (even an empty one) so a status can be added to it; a status whose stage
 * the master list does not hold (still loading, or failed) still gets a group,
 * named from the row itself.
 */
function groupByStage(
  rows: readonly CompanyStageStatus[],
  stages: readonly ReferenceRecord[],
): StageGroup[] {
  const groups = new Map<number, StageGroup>();
  for (const stage of stages) {
    groups.set(stage.id, {
      stage: { id: stage.id, name: stage.name },
      stageActive: stage.isActive,
      rows: [],
    });
  }
  for (const row of rows) {
    let group = groups.get(row.stage.id);
    if (!group) {
      group = { stage: row.stage, stageActive: null, rows: [] };
      groups.set(row.stage.id, group);
    }
    group.rows.push(row);
  }
  return [...groups.values()];
}

function writeFailure(caught: unknown, ability: Ability, nothing: string): string {
  if (caught instanceof ApiError) {
    if (caught.status === 403) {
      return `${adminWriteDenial(ability, MODULE) ?? 'The server refused that.'} ${nothing}`;
    }
    return caught.message;
  }
  return `Couldn’t reach the server. ${nothing}`;
}

export function CompanyStageStatusesCard({
  ability,
  stages,
  stagesLoading,
  stagesFailed,
}: {
  ability: Ability;
  /** The `/company-stages` master the page already loaded (active and retired). */
  stages: readonly ReferenceRecord[];
  stagesLoading: boolean;
  stagesFailed: boolean;
}) {
  const [state, setState] = useState<ListState>({ rows: null, error: null, loading: true });
  const [nonce, setNonce] = useState(0);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    const { signal } = controller;

    // Deferred a tick — same reason as every other list on this page.
    const timer = setTimeout(() => {
      setState((previous) => ({ ...previous, loading: true }));
      void api
        .listCompanyStageStatuses({}, signal)
        .then((result) => {
          if (signal.aborted) return;
          setState({ rows: result.data, error: null, loading: false });
        })
        .catch((caught: unknown) => {
          if (signal.aborted) return;
          setState((previous) => ({
            ...previous,
            error: caught instanceof Error ? caught.message : 'Failed to load stage statuses',
          }));
        })
        .finally(() => {
          if (!signal.aborted) setState((previous) => ({ ...previous, loading: false }));
        });
    }, 0);

    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [nonce]);

  const reload = useCallback(() => {
    invalidateCompanyStageStatuses();
    setNonce((value) => value + 1);
  }, []);

  const rows = useMemo(() => state.rows ?? [], [state.rows]);
  const retired = rows.filter((row) => !row.isActive).length;

  return (
    <>
      <CatalogMasterCard
        icon={<StageStatusIcon />}
        title="Stage statuses"
        loading={state.loading && state.rows === null}
        error={state.error}
        badges={
          <>
            <Badge tone="neutral" variant="outline">
              {pluralise(rows.length, 'status', 'statuses')}
            </Badge>
            {retired > 0 && (
              <Badge tone="neutral" variant="outline">
                {retired} retired
              </Badge>
            )}
          </>
        }
        canWrite={canWriteAdminMaster(ability, MODULE)}
        onOpen={() => setOpen(true)}
      />

      {open && (
        <StageStatusListModal
          onClose={() => setOpen(false)}
          state={state}
          rows={rows}
          stages={stages}
          stagesLoading={stagesLoading}
          stagesFailed={stagesFailed}
          ability={ability}
          onReload={reload}
        />
      )}
    </>
  );
}

function StageStatusListModal({
  onClose,
  state,
  rows,
  stages,
  stagesLoading,
  stagesFailed,
  ability,
  onReload,
}: {
  onClose: () => void;
  state: ListState;
  rows: CompanyStageStatus[];
  stages: readonly ReferenceRecord[];
  stagesLoading: boolean;
  stagesFailed: boolean;
  ability: Ability;
  onReload: () => void;
}) {
  const [form, setForm] = useState<
    { mode: 'create'; stageId: number | null } | { mode: 'edit'; record: CompanyStageStatus } | null
  >(null);
  const [deleting, setDeleting] = useState<CompanyStageStatus | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [rowError, setRowError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const canUpdate = canWriteAdminMaster(ability, MODULE);
  const canCreate = ability.canCreate(MODULE);
  const canDelete = ability.canDelete(MODULE);
  const anyWrite = canUpdate || canCreate || canDelete;
  const denial = adminWriteDenial(ability, MODULE);

  const groups = useMemo(() => groupByStage(rows, stages), [rows, stages]);
  const initialLoad = state.loading && state.rows === null;
  const activeStages = stages.filter((stage) => stage.isActive);

  /** Retire or restore, as a one-key PATCH. */
  async function setActive(row: CompanyStageStatus, isActive: boolean) {
    if (busy) return;
    setBusy(true);
    setRowError(null);
    try {
      await api.updateCompanyStageStatus(row.id, { isActive });
      onReload();
    } catch (caught) {
      setRowError(writeFailure(caught, ability, 'Nothing was changed.'));
    } finally {
      setBusy(false);
    }
  }

  /**
   * Move one row up or down within its stage. Renumbers the stage's rows
   * 1..n and PATCHes only the rows whose `sortOrder` changes, one at a time
   * (there is no bulk endpoint). A failure part-way is reported; the list is
   * reloaded either way so it shows what was saved.
   */
  async function move(group: StageGroup, index: number, delta: -1 | 1) {
    if (busy) return;
    const target = index + delta;
    if (target < 0 || target >= group.rows.length) return;
    const ordered = [...group.rows];
    const [moved] = ordered.splice(index, 1);
    if (!moved) return;
    ordered.splice(target, 0, moved);

    setBusy(true);
    setRowError(null);
    let saved = 0;
    try {
      for (const [position, row] of ordered.entries()) {
        const sortOrder = position + 1;
        if (row.sortOrder === sortOrder) continue;
        await api.updateCompanyStageStatus(row.id, { sortOrder });
        saved += 1;
      }
    } catch (caught) {
      const prefix = saved > 0 ? `The new order was only partly saved (${saved} saved). ` : '';
      setRowError(prefix + writeFailure(caught, ability, saved > 0 ? '' : 'Nothing was changed.'));
    } finally {
      setBusy(false);
      onReload();
    }
  }

  async function confirmDelete() {
    if (!deleting || busy) return;
    setBusy(true);
    setDeleteError(null);
    try {
      await api.deleteCompanyStageStatus(deleting.id);
      setDeleting(null);
      onReload();
    } catch (caught) {
      if (caught instanceof ApiError && caught.code === 'STAGE_STATUS_IN_USE') {
        setDeleteError(
          `${caught.message} Close this and use “Retire” on the row instead — companies holding it keep showing it.`,
        );
      } else if (caught instanceof ApiError && caught.status === 404) {
        // Already gone: that is the outcome the user asked for.
        setDeleting(null);
        onReload();
      } else {
        setDeleteError(writeFailure(caught, ability, 'Nothing was deleted.'));
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <Modal
        open
        onClose={onClose}
        title="Stage statuses"
        size="lg"
        footer={
          <>
            <Button variant="secondary" onClick={onClose}>
              Close
            </Button>
            {canCreate && (
              <Button
                variant="primary"
                leadingIcon={<PlusIcon />}
                disabled={activeStages.length === 0}
                onClick={() => setForm({ mode: 'create', stageId: null })}
              >
                New stage status
              </Button>
            )}
          </>
        }
      >
        <div className="grid gap-4">
          {state.error && (
            <CatalogErrorBlock>Couldn’t load stage statuses. {state.error}</CatalogErrorBlock>
          )}
          {stagesFailed && (
            <CatalogErrorBlock>
              Couldn’t load company stages, so new statuses can’t be added.
            </CatalogErrorBlock>
          )}
          {rowError && <CatalogErrorBlock>{rowError}</CatalogErrorBlock>}
          {denial && <p className="m-0 text-xs text-fg-subtle">{denial} You can read it here.</p>}

          {initialLoad || (stagesLoading && groups.length === 0) ? (
            <TableWrap>
              <Table className="min-w-[720px]">
                <StageStatusHead showActions={anyWrite} />
                <TBody>
                  {Array.from({ length: SKELETON_ROWS }, (_, index) => (
                    <TR key={index}>
                      {Array.from({ length: 4 + (anyWrite ? 1 : 0) }, (_, cell) => (
                        <TD key={cell}>
                          <Skeleton className={cx('h-3', cell === 0 ? 'w-40' : 'w-16')} />
                        </TD>
                      ))}
                    </TR>
                  ))}
                </TBody>
              </Table>
            </TableWrap>
          ) : groups.length === 0 ? (
            <EmptyState
              icon={<StageStatusIcon />}
              title={state.error || stagesFailed ? 'Nothing to show' : 'No company stages configured'}
              description={
                state.error || stagesFailed
                  ? 'Couldn’t load the list — see the message above.'
                  : 'Add a company stage first; stage statuses sit under a stage.'
              }
            />
          ) : (
            groups.map((group) => (
              <section key={group.stage.id} aria-label={group.stage.name} className="grid gap-2">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <h3 className="m-0 flex items-center gap-2 text-sm font-semibold text-fg">
                    {group.stage.name}
                    {group.stageActive === false && <CatalogStatusBadge active={false} />}
                  </h3>
                  {canCreate && group.stageActive === true && (
                    <Button
                      variant="ghost"
                      size="sm"
                      leadingIcon={<PlusIcon />}
                      disabled={busy}
                      onClick={() => setForm({ mode: 'create', stageId: group.stage.id })}
                    >
                      <span className="sr-only">Add a status to {group.stage.name}</span>
                      <span aria-hidden="true">Add</span>
                    </Button>
                  )}
                </div>

                {group.rows.length === 0 ? (
                  <p className="m-0 text-xs text-fg-subtle">No statuses</p>
                ) : (
                  <TableWrap>
                    <Table className="min-w-[720px]">
                      <StageStatusHead showActions={anyWrite} />
                      <TBody>
                        {group.rows.map((row, index) => (
                          <TR key={row.id}>
                            <TH scope="row" className="min-w-[9rem]">
                              <span className="break-words">{row.name}</span>
                            </TH>
                            <TD>
                              <BehaviourBadges row={row} />
                            </TD>
                            <TD numeric className="text-xs text-fg-muted tabular">
                              {row.sortOrder}
                            </TD>
                            <TD>
                              <CatalogStatusBadge active={row.isActive} />
                            </TD>
                            {anyWrite && (
                              <TD>
                                <div className="flex items-center justify-end gap-1">
                                  {canUpdate && (
                                    <>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        iconOnly
                                        aria-label={`Move ${row.name} up`}
                                        title="Move up"
                                        disabled={busy || index === 0}
                                        leadingIcon={<ChevronIcon direction="up" />}
                                        onClick={() => void move(group, index, -1)}
                                      />
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        iconOnly
                                        aria-label={`Move ${row.name} down`}
                                        title="Move down"
                                        disabled={busy || index === group.rows.length - 1}
                                        leadingIcon={<ChevronIcon direction="down" />}
                                        onClick={() => void move(group, index, 1)}
                                      />
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        iconOnly
                                        aria-label={`Edit ${row.name}`}
                                        title={`Edit ${row.name}`}
                                        disabled={busy}
                                        leadingIcon={<PencilIcon />}
                                        onClick={() => setForm({ mode: 'edit', record: row })}
                                      />
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        disabled={busy}
                                        onClick={() => void setActive(row, !row.isActive)}
                                      >
                                        <span className="sr-only">
                                          {row.isActive ? `Retire ${row.name}` : `Restore ${row.name}`}
                                        </span>
                                        <span aria-hidden="true">
                                          {row.isActive ? 'Retire' : 'Restore'}
                                        </span>
                                      </Button>
                                    </>
                                  )}
                                  {canDelete && (
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      iconOnly
                                      aria-label={`Delete ${row.name} permanently`}
                                      title={`Delete ${row.name} permanently`}
                                      disabled={busy}
                                      leadingIcon={<TrashIcon />}
                                      className="text-fg-subtle hover:text-danger"
                                      onClick={() => {
                                        setDeleteError(null);
                                        setDeleting(row);
                                      }}
                                    />
                                  )}
                                </div>
                              </TD>
                            )}
                          </TR>
                        ))}
                      </TBody>
                    </Table>
                  </TableWrap>
                )}
              </section>
            ))
          )}
        </div>
      </Modal>

      {form && (
        <StageStatusFormModal
          record={form.mode === 'edit' ? form.record : null}
          initialStageId={form.mode === 'create' ? form.stageId : null}
          stages={activeStages}
          ability={ability}
          onClose={() => setForm(null)}
          onSaved={() => {
            setForm(null);
            onReload();
          }}
        />
      )}

      <ConfirmDialog
        open={deleting !== null}
        onClose={() => {
          setDeleting(null);
          setDeleteError(null);
        }}
        onConfirm={() => void confirmDelete()}
        title={`Delete “${deleting?.name ?? ''}” permanently?`}
        confirmLabel="Delete status"
        loading={busy}
        error={deleteError}
        description={
          <>
            <p className="m-0">
              The stage status <strong>{deleting?.name}</strong> under{' '}
              <strong>{deleting?.stage.name}</strong> will be deleted. This can't be undone.
            </p>
            <p className="mt-2 mb-0">
              A status that a company still holds can't be deleted. Retire it instead.
            </p>
          </>
        }
      />
    </>
  );
}

function StageStatusHead({ showActions }: { showActions: boolean }) {
  return (
    <THead>
      <TR>
        <TH>Status</TH>
        <TH>Behaviour</TH>
        <TH numeric className="w-20">
          Order
        </TH>
        <TH className="w-28">Active</TH>
        {showActions && (
          <TH className="w-64">
            <span className="sr-only">Actions</span>
          </TH>
        )}
      </TR>
    </THead>
  );
}

interface FormValues {
  stageId: string;
  name: string;
  sortOrder: string;
  isActive: boolean;
  /** Blank = no SLA. */
  followUpSlaDays: string;
  resurfaceMode: '' | StageStatusResurfaceMode;
  excludesFollowUp: boolean;
  requiresReason: boolean;
  suppressesMarketing: boolean;
}

const BEHAVIOUR_KEYS = [
  'followUpSlaDays',
  'resurfaceMode',
  'excludesFollowUp',
  'requiresReason',
  'suppressesMarketing',
] as const;

function initialFormValues(record: CompanyStageStatus | null, initialStageId: number | null): FormValues {
  if (!record) {
    return {
      stageId: initialStageId !== null ? String(initialStageId) : '',
      name: '',
      sortOrder: '',
      isActive: true,
      followUpSlaDays: '',
      resurfaceMode: '',
      excludesFollowUp: false,
      requiresReason: false,
      suppressesMarketing: false,
    };
  }
  return {
    stageId: String(record.stage.id),
    name: record.name,
    sortOrder: String(record.sortOrder),
    isActive: record.isActive,
    followUpSlaDays: typeof record.followUpSlaDays === 'number' ? String(record.followUpSlaDays) : '',
    resurfaceMode: record.resurfaceMode ?? '',
    excludesFollowUp: record.excludesFollowUp === true,
    requiresReason: record.requiresReason === true,
    suppressesMarketing: record.suppressesMarketing === true,
  };
}

/** Create and edit in one form. `record: null` is create. */
function StageStatusFormModal({
  record,
  initialStageId,
  stages,
  ability,
  onClose,
  onSaved,
}: {
  record: CompanyStageStatus | null;
  initialStageId: number | null;
  /** ACTIVE stages only: a retired stage refuses a new status (422). */
  stages: readonly ReferenceRecord[];
  ability: Ability;
  onClose: () => void;
  onSaved: () => void;
}) {
  const isEdit = record !== null;
  const [initial] = useState<FormValues>(() => initialFormValues(record, initialStageId));
  const [values, setValues] = useState<FormValues>(initial);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const patch = useMemo(
    () => (record ? buildPatch(values, initial, record) : null),
    [values, initial, record],
  );
  const dirty = patch === null || Object.keys(patch).length > 0;

  function setField<K extends keyof FormValues>(key: K, value: FormValues[K]) {
    setValues((previous) => {
      const next = { ...previous, [key]: value };
      // A follow-up SLA and a resurface mode are mutually exclusive.
      if (key === 'followUpSlaDays' && value !== '') next.resurfaceMode = '';
      if (key === 'resurfaceMode' && value !== '') next.followUpSlaDays = '';
      return next;
    });
    setFieldErrors((previous) => {
      const cleared: string[] = [key];
      if (key === 'followUpSlaDays' || key === 'resurfaceMode') {
        cleared.push('followUpSlaDays', 'resurfaceMode');
      }
      if (!cleared.some((k) => k in previous)) return previous;
      const next = { ...previous };
      for (const k of cleared) delete next[k];
      return next;
    });
  }

  function validate(): Record<string, string> {
    const errors: Record<string, string> = {};
    if (!isEdit && values.stageId === '') errors.stageId = 'Choose a stage.';
    if (values.name.trim() === '') errors.name = 'A name is required.';
    const sortOrder = values.sortOrder.trim();
    if (sortOrder !== '') {
      const parsed = Number(sortOrder);
      if (!Number.isInteger(parsed) || parsed < SORT_ORDER_MIN || parsed > SORT_ORDER_MAX) {
        errors.sortOrder = `Enter a whole number from ${SORT_ORDER_MIN} to ${SORT_ORDER_MAX}.`;
      }
    }
    const sla = values.followUpSlaDays.trim();
    if (sla !== '') {
      const parsed = Number(sla);
      if (!Number.isInteger(parsed) || parsed < SLA_MIN || parsed > SLA_MAX) {
        errors.followUpSlaDays = `Enter a whole number from ${SLA_MIN} to ${SLA_MAX}.`;
      }
    }
    return errors;
  }

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    event.stopPropagation();
    if (saving || !dirty) return;

    const clientErrors = validate();
    if (Object.keys(clientErrors).length > 0) {
      setFieldErrors(clientErrors);
      setFormError(null);
      return;
    }

    setSaving(true);
    setFieldErrors({});
    setFormError(null);
    try {
      if (record && patch) {
        await api.updateCompanyStageStatus(record.id, patch);
      } else {
        await api.createCompanyStageStatus(buildCreate(values));
      }
      onSaved();
    } catch (caught) {
      if (caught instanceof ApiError) {
        if (caught.code === 'STAGE_STATUS_NAME_TAKEN') {
          setFieldErrors({ name: caught.message });
        } else if (caught.code === 'STAGE_STATUS_BEHAVIOUR_IN_USE') {
          // A form-level refusal: the server's sentence names what holds the
          // behaviour, and no single field is wrong.
          setFieldErrors({});
          setFormError(caught.message);
        } else if (caught.code === 'STAGE_STATUS_BEHAVIOUR_CONFLICT') {
          const fields = caught.fieldErrors;
          setFieldErrors(fields);
          setFormError(
            Object.keys(fields).length > 0 ? caught.formMessage : (caught.formMessage ?? caught.message),
          );
        } else if (caught.status === 403) {
          setFormError(
            `${adminWriteDenial(ability, MODULE) ?? 'The server refused that.'} Nothing was saved.`,
          );
        } else if (caught.status === 404) {
          setFormError('That stage status no longer exists. Close this and reload the list.');
        } else {
          const known = isEdit
            ? ['name', 'sortOrder', 'isActive', ...BEHAVIOUR_KEYS]
            : ['stageId', 'name', 'sortOrder', 'isActive', ...BEHAVIOUR_KEYS];
          const routed = caught.routeFieldErrors(known);
          const lines = [...routed.unroutable];
          const generic = caught.message.trim().toLowerCase() === 'validation failed';
          if (!generic && caught.message && !lines.includes(caught.message)) {
            lines.unshift(caught.message);
          }
          setFieldErrors(routed.fields);
          setFormError(lines.length > 0 ? lines.join(' ') : generic ? null : caught.message);
        }
      } else {
        setFormError('Couldn’t reach the server. Nothing was saved.');
      }
    } finally {
      setSaving(false);
    }
  }

  const formId = `stage-status-form-${record?.id ?? 'new'}`;

  return (
    <Modal
      open
      onClose={onClose}
      title={isEdit ? `Edit ${record.name}` : 'New stage status'}
      dismissOnBackdrop={false}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button type="submit" form={formId} variant="primary" loading={saving} disabled={!dirty}>
            {isEdit ? 'Save changes' : 'Create stage status'}
          </Button>
        </>
      }
    >
      <form id={formId} onSubmit={handleSubmit} noValidate className="grid gap-4">
        <FormErrorSummary formError={formError} fieldErrorCount={Object.keys(fieldErrors).length} />

        {/* The stage is fixed once created: PATCH does not accept it. */}
        {isEdit ? (
          <Field label="Company stage" hint="The stage can’t be changed. Delete the status and add it again under another stage.">
            {({ id, 'aria-describedby': describedBy }) => (
              <Input
                id={id}
                aria-describedby={describedBy}
                value={record.stage.name}
                readOnly
                disabled
              />
            )}
          </Field>
        ) : (
          <Field label="Company stage" required error={fieldErrors.stageId}>
            {({ id, 'aria-describedby': describedBy }) => (
              <Select
                id={id}
                aria-describedby={describedBy}
                value={values.stageId}
                invalid={Boolean(fieldErrors.stageId)}
                onChange={(event) => setField('stageId', event.target.value)}
              >
                <option value="">Choose a stage</option>
                {stages.map((stage) => (
                  <option key={stage.id} value={String(stage.id)}>
                    {stage.name}
                  </option>
                ))}
              </Select>
            )}
          </Field>
        )}

        <Field label="Name" required error={fieldErrors.name}>
          {({ id, 'aria-describedby': describedBy }) => (
            <Input
              id={id}
              aria-describedby={describedBy}
              value={values.name}
              maxLength={NAME_MAX}
              autoComplete="off"
              invalid={Boolean(fieldErrors.name)}
              onChange={(event) => setField('name', event.target.value)}
            />
          )}
        </Field>

        <Field label="Sort order" error={fieldErrors.sortOrder}>
          {({ id, 'aria-describedby': describedBy }) => (
            <Input
              id={id}
              aria-describedby={describedBy}
              type="number"
              inputMode="numeric"
              min={SORT_ORDER_MIN}
              max={SORT_ORDER_MAX}
              step={1}
              value={values.sortOrder}
              invalid={Boolean(fieldErrors.sortOrder)}
              onChange={(event) => setField('sortOrder', event.target.value)}
            />
          )}
        </Field>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Follow-up within (days)" error={fieldErrors.followUpSlaDays}>
            {({ id, 'aria-describedby': describedBy }) => (
              <Input
                id={id}
                aria-describedby={describedBy}
                type="number"
                inputMode="numeric"
                min={SLA_MIN}
                max={SLA_MAX}
                step={1}
                value={values.followUpSlaDays}
                invalid={Boolean(fieldErrors.followUpSlaDays)}
                onChange={(event) => setField('followUpSlaDays', event.target.value)}
              />
            )}
          </Field>

          <Field label="Resurface" error={fieldErrors.resurfaceMode}>
            {({ id, 'aria-describedby': describedBy }) => (
              <Select
                id={id}
                aria-describedby={describedBy}
                value={values.resurfaceMode}
                invalid={Boolean(fieldErrors.resurfaceMode)}
                onChange={(event) =>
                  setField('resurfaceMode', event.target.value as FormValues['resurfaceMode'])
                }
              >
                {RESURFACE_OPTIONS.map((option) => (
                  <option key={option.value || 'none'} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Select>
            )}
          </Field>
        </div>

        <div className="grid gap-2">
          <Checkbox
            checked={values.excludesFollowUp}
            onChange={(event) => setField('excludesFollowUp', event.target.checked)}
            label="Leave out of follow-ups"
          />
          <Checkbox
            checked={values.requiresReason}
            onChange={(event) => setField('requiresReason', event.target.checked)}
            label="Ask for a reason"
          />
          <Checkbox
            checked={values.suppressesMarketing}
            onChange={(event) => setField('suppressesMarketing', event.target.checked)}
            label="Stop marketing emails"
          />
          <Checkbox
            checked={values.isActive}
            onChange={(event) => setField('isActive', event.target.checked)}
            label="Active"
          />
        </div>
      </form>
    </Modal>
  );
}

/** Blank → null (no SLA). Only called after `validate()` passed. */
function slaOf(value: string): number | null {
  const trimmed = value.trim();
  return trimmed === '' ? null : Number(trimmed);
}

/**
 * Only what changed. A blank Sort order sends nothing (NOT NULL, server default).
 * Behaviour keys are diffed against the form's INITIAL values, not the record,
 * so a field an older API omitted is not sent unless someone touched it.
 */
function buildPatch(
  values: FormValues,
  initial: FormValues,
  record: CompanyStageStatus,
): CompanyStageStatusUpdateInput {
  const patch: CompanyStageStatusUpdateInput = {};
  const name = values.name.trim();
  if (name !== record.name) patch.name = name;
  const sortOrder = values.sortOrder.trim();
  if (sortOrder !== '' && Number(sortOrder) !== record.sortOrder) patch.sortOrder = Number(sortOrder);
  if (values.isActive !== record.isActive) patch.isActive = values.isActive;
  if (values.followUpSlaDays.trim() !== initial.followUpSlaDays.trim()) {
    patch.followUpSlaDays = slaOf(values.followUpSlaDays);
  }
  if (values.resurfaceMode !== initial.resurfaceMode) {
    patch.resurfaceMode = values.resurfaceMode === '' ? null : values.resurfaceMode;
  }
  if (values.excludesFollowUp !== initial.excludesFollowUp) {
    patch.excludesFollowUp = values.excludesFollowUp;
  }
  if (values.requiresReason !== initial.requiresReason) patch.requiresReason = values.requiresReason;
  if (values.suppressesMarketing !== initial.suppressesMarketing) {
    patch.suppressesMarketing = values.suppressesMarketing;
  }
  return patch;
}

/** Built from an explicit key list (the schema is `.strict()`). */
function buildCreate(values: FormValues): CompanyStageStatusCreateInput {
  const body: CompanyStageStatusCreateInput = {
    stageId: Number(values.stageId),
    name: values.name.trim(),
    isActive: values.isActive,
  };
  const sortOrder = values.sortOrder.trim();
  if (sortOrder !== '') body.sortOrder = Number(sortOrder);
  // Defaults (null / false) are the server's own: send only what was set.
  const sla = slaOf(values.followUpSlaDays);
  if (sla !== null) body.followUpSlaDays = sla;
  if (values.resurfaceMode !== '') body.resurfaceMode = values.resurfaceMode;
  if (values.excludesFollowUp) body.excludesFollowUp = true;
  if (values.requiresReason) body.requiresReason = true;
  if (values.suppressesMarketing) body.suppressesMarketing = true;
  return body;
}

/** A stage marker with a branch beneath it — a status under a stage. */
function StageStatusIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="h-5 w-5"
    >
      <rect x="3" y="3" width="8" height="5" rx="1" />
      <path d="M7 8v9a2 2 0 0 0 2 2h4" />
      <path d="M7 12h6" />
      <rect x="13" y="10" width="8" height="4" rx="1" />
      <rect x="13" y="17" width="8" height="4" rx="1" />
    </svg>
  );
}
